import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'

import { styled } from 'styled-components'

import Textarea from 'core/components/lib/Textarea/Textarea'

type JsonTextareaProps = {
  value: unknown
  onChange?: (value: unknown) => void
  placeholder?: string
  autoFocus?: boolean
  /**
   * When true, the component treats `value` as a raw JSON string and will not
   * stringify it for display. It will still validate the JSON and show errors,
   * but will call onChange with the raw string so callers can manage it.
   */
  stringMode?: boolean
  /**
   * Optional callback when JSON validation state changes.
   * Called with true when JSON is valid, false when invalid.
   * Does not affect existing behavior - purely additive.
   */
  onValidationChange?: (isValid: boolean) => void
  /**
   * Number of visible rows (default: 6)
   */
  rows?: number
  /**
   * Maximum number of rows before scrolling (optional)
   */
  maxRows?: number
}

// JSON editor component for complex fields in DynamicForm
const JsonTextarea: FC<JsonTextareaProps> = ({
  value,
  onChange,
  placeholder,
  autoFocus,
  stringMode = false,
  onValidationChange,
  rows = 6,
  maxRows,
}) => {
  const [jsonString, setJsonString] = useState(() => {
    if (stringMode) {
      return typeof value === 'string' ? (value as string) : ''
    }
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return ''
    }
  })
  const [error, setError] = useState('')
  const isUserTypingRef = useRef(false)

  const handleChange = (newJsonString: string) => {
    isUserTypingRef.current = true
    setJsonString(newJsonString)

    try {
      const parsed = JSON.parse(newJsonString)
      setError('')
      onValidationChange?.(true)
      onChange?.(stringMode ? newJsonString : parsed)
    } catch (parseError) {
      setError(
        `Invalid JSON: ${
          parseError instanceof Error ? parseError.message : 'Unknown error'
        }`,
      )
      onValidationChange?.(false)
      // In stringMode, still pass the raw string up so caller can manage state
      if (stringMode) onChange?.(newJsonString)
    }

    // Reset typing flag after a short delay
    setTimeout(() => {
      isUserTypingRef.current = false
    }, 100)
  }

  // Update local state when external value changes (but not when user is typing)
  useEffect(() => {
    if (isUserTypingRef.current) return // Don't update while user is typing

    try {
      const newJsonString = stringMode
        ? typeof value === 'string'
          ? (value as string)
          : ''
        : JSON.stringify(value, null, 2)
      setJsonString(newJsonString)
      setError('')
    } catch {
      // Keep current string if new value can't be stringified
    }
  }, [value, stringMode])

  const INDENT = '  '

  const getLineIndent = (text: string, pos: number): string => {
    const prevNewline = text.lastIndexOf('\n', pos - 1)
    const lineStart = prevNewline === -1 ? 0 : prevNewline + 1
    let i = lineStart
    while (i < text.length && i < pos && (text[i] === ' ' || text[i] === '\t'))
      i++
    return text.slice(lineStart, i)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const dispatchNativeInput = (el: HTMLTextAreaElement) => {
      try {
        const evt =
          typeof InputEvent !== 'undefined'
            ? new InputEvent('input', {
                bubbles: true,
                inputType: 'insertText',
              })
            : new Event('input', { bubbles: true })
        el.dispatchEvent(evt)
      } catch {}
    }

    const insertWithHistory = (
      el: HTMLTextAreaElement,
      text: string,
      caretAbsolutePos?: number,
    ) => {
      const start = el.selectionStart ?? 0
      const end = el.selectionEnd ?? 0
      let usedExec = false
      try {
        // execCommand is deprecated but preserves undo stack better
        // eslint-disable-next-line deprecation/deprecation
        usedExec = document.execCommand?.('insertText', false, text) ?? false
      } catch {
        usedExec = false
      }
      if (!usedExec) {
        el.setRangeText(text, start, end, 'end')
        dispatchNativeInput(el)
      }
      const target =
        typeof caretAbsolutePos === 'number'
          ? caretAbsolutePos
          : start + text.length
      setTimeout(() => {
        try {
          el.selectionStart = el.selectionEnd = target
        } catch {}
      }, 0)
    }
    if (e.key === 'Tab') {
      e.preventDefault()
      const el = e.target as HTMLTextAreaElement
      const insert = INDENT
      insertWithHistory(el, insert)
      return
    }

    if (e.key === '{' || e.key === '[') {
      e.preventDefault()
      const el = e.target as HTMLTextAreaElement
      const start = el.selectionStart ?? 0

      const open = e.key
      const close = open === '{' ? '}' : ']'

      // Indentation based on the line's leading whitespace
      const baseIndent = getLineIndent(jsonString, start)
      const innerIndent = baseIndent + INDENT

      const inserted = `${open}\n${innerIndent}\n${baseIndent}${close}`
      const caretPos = start + `${open}\n${innerIndent}`.length
      insertWithHistory(el, inserted, caretPos)
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      const el = e.target as HTMLTextAreaElement
      const start = el.selectionStart ?? 0

      const baseIndent = getLineIndent(jsonString, start)

      const insert = `\n${baseIndent}`
      insertWithHistory(el, insert)
    }
  }

  return (
    <div>
      <StyledTextArea
        autoFocus={autoFocus}
        maxRows={maxRows}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        value={jsonString}
      />
      {error && <ErrorText>{error}</ErrorText>}
      {!error && jsonString && <SuccessText>✅ Valid JSON</SuccessText>}
    </div>
  )
}

export default JsonTextarea

const StyledTextArea = styled(Textarea)`
  line-height: 1.4;
  font-family: Monaco, Menlo, 'Ubuntu Mono', monospace;
  font-size: 12px;
`

const ErrorText = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.error || 'red'};
  font-size: 12px;
`

const SuccessText = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.success || 'green'};
  font-size: 12px;
`
